import { useState, useCallback, useRef } from 'react'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useUser } from '../../../hooks/useUser'
import { usePagination } from '../../../hooks/usePagination'
import { getInboundAdjustSku, putInboundAdjustSku } from '../config/api'
import { AsnAdjustSkuUpdate } from '../../../interfaces/Inbound/InboundAdjust'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { WorkOrderInboundSkuAdjust } from '../modals/Entities/InboundAdjust'

// Tipos para el estado local de las filas
type RowQuantities = Record<number, { missingQty: number; qualityQty: number }>
type RowComments = Record<number, string>

// Hook principal que manejará la lógica de ajustes
export const useAdjustments = (
  workOrderId: number,
  initialSkuId: number,
  initialLpn?: string,
  onDataReload?: () => void
) => {
  // --- ESTADOS ---
  const [items, setItems] = useState<WorkOrderInboundSkuAdjust[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [rowQuantities, setRowQuantities] = useState<RowQuantities>({})
  const [rowComments, setRowComments] = useState<RowComments>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const [saveButtonState, setSaveButtonState] = useState<boolean>(true)

  // --- HOOKS Y REFERENCIAS---
  const pagination = usePagination(5)
  const hasFetched = useRef(false)
  const {
    userDataState: { userData }
  } = useUser()
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()
  const { callApi: fetchSkuDetails } = useApi<WorkOrderInboundSkuAdjust[]>()
  const { callApi: sendAdjustmentsApi } = useApi<AsnAdjustSkuUpdate[]>()

  // --- LÓGICA DE OBTENCIÓN DE DATOS ---
  const requestInboundAdjust = useCallback(
    (page: number, pageSize: number, filters?: Filter[]) => {
      setLoading(true)
      fetchSkuDetails(getInboundAdjustSku(workOrderId, page, pageSize, filters))
        .then((data) => {
          setItems(data.items as WorkOrderInboundSkuAdjust[])
          setTotalItems(data.totalItems)
          pagination.setPagination(page, pageSize)
        })
        .catch((err) =>
          openSnackBar(err.message || 'Error al consultar lotes', false)
        )
        .finally(() => setLoading(false))
    },
    [
      workOrderId,
      initialSkuId,
      initialLpn,
      fetchSkuDetails,
      openSnackBar,
      pagination
    ]
  )

  // Función para la carga inicial hasFetched
  const fetchInitialData = useCallback(
    (newFilters: Filter[]) => {
      if (hasFetched.current) return
      hasFetched.current = true

      setSelectedFilters(newFilters)
      requestInboundAdjust(pagination.page, pagination.pageSize, newFilters)
    },
    [requestInboundAdjust, pagination]
  )

  // Manejador para la paginación
  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestInboundAdjust(auxPage, pageSize, selectedFilters)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleSearch = (newFilters: Filter[]) => {
    const page = 0 // Siempre que se filtra, volvemos a la página 0
    setSelectedFilters(newFilters) // Guardamos los nuevos filtros en el estado
    setRowQuantities({}) // Limpiamos las cantidades de calidad y faltante
    setRowComments({}) // Limpiamos los comentarios
    requestInboundAdjust(page, pagination.pageSize, newFilters) // Llamamos a la API
    pagination.setPagination(page, pagination.pageSize)
    setSaveButtonState(true)
  }

  // --- MAPPERS ---
  const mapSingleItemToPayload = (
    rowIndex: number,
    asnDetailId: number
  ): AsnAdjustSkuUpdate => {
    const item = items[rowIndex]
    return {
      workOrderId: workOrderId,
      skuId: item.skuId,
      salePositions: [],
      asnDetailId: asnDetailId,
      reductionQty: rowQuantities[rowIndex]?.qualityQty || null,
      missingQty: rowQuantities[rowIndex]?.missingQty || null,
      excessQty: null,
      userId: userData?.userId || 0,
      batchId: item.batchId,
      comment: rowComments[rowIndex] || '',
      lpn: item.lpn
    }
  }

  // --- VALIDACIONES ---
  const validateSkuQty = (rowIndex: number): boolean => {
    const item = items[rowIndex]
    const quantities = rowQuantities[rowIndex] || {
      missingQty: 0,
      qualityQty: 0
    }
    const totalQty = quantities.missingQty + quantities.qualityQty

    if (items[rowIndex]?.skuReleased) return true

    if (item.receptionQty !== totalQty) {
      openSnackBar(
        'La suma de "Faltante" y "Calidad" debe ser igual a las unidades pendientes por ajustar.',
        false
      )
      return false
    }
    if (!rowComments[rowIndex]?.trim()) {
      openSnackBar(
        'El comentario es obligatorio para realizar el ajuste.',
        false
      )
      return false
    }
    return true
  }

  // --- MANEJADORES DE EVENTOS (HANDLERS) ---
  const handleQuantityChange = (
    rowIndex: number,
    fieldType: 'missingQty' | 'qualityQty',
    value: string
  ) => {
    const newQty = value === '' ? 0 : parseInt(value, 10)

    const currentRow = rowQuantities[rowIndex] || {
      missingQty: 0,
      qualityQty: 0
    }
    const maxAllowed = items[rowIndex].receptionQty

    const missingQty =
      fieldType === 'missingQty' ? newQty : currentRow.missingQty
    const qualityQty =
      fieldType === 'qualityQty' ? newQty : currentRow.qualityQty
    const totalRowQty = missingQty + qualityQty

    if (isNaN(newQty) || newQty < 0 || totalRowQty > maxAllowed) return

    const updatedQuantities = {
      ...rowQuantities,
      [rowIndex]: { missingQty, qualityQty }
    }

    const hasAnyValue = Object.values(updatedQuantities).some(
      (row) => row.missingQty + row.qualityQty > 0
    )

    setSaveButtonState(!hasAnyValue)
    setRowQuantities(updatedQuantities)
  }

  const handleCommentChange = (rowIndex: number, value: string) => {
    setRowComments((prevState) => ({ ...prevState, [rowIndex]: value }))
  }

  // --- ACCIONES DE GUARDADO ---
  const saveLine = async (rowIndex: number, asnDetailId: number) => {
    if (items[rowIndex]?.skuReleased) {
      openSnackBar('Esta línea ya fue ajustada y no puede modificarse.', false)
      return
    }

    if (!validateSkuQty(rowIndex)) return

    setLoading(true)
    try {
      const payload = [mapSingleItemToPayload(rowIndex, asnDetailId)]
      await sendAdjustmentsApi(putInboundAdjustSku(payload))
      openSnackBar('Línea guardada con éxito', true)
      if (onDataReload) {
        onDataReload()
      } else {
        await requestInboundAdjust(
          pagination.page,
          pagination.pageSize,
          selectedFilters
        )
      }
    } catch (error) {
      openSnackBar(`Error al guardar: ${error}`, false)
    } finally {
      setLoading(false)
    }
  }

  const saveAll = async () => {
    setLoading(true)
    try {
      const itemsToAdjust = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !item.skuReleased)

      // Validamos solo los items que vamos a enviar
      for (const { index } of itemsToAdjust) {
        if (!validateSkuQty(index)) {
          openSnackBar(
            `Por favor, complete y/o corriga los datos de la línea ${
              index + 1
            } para poder continuar`,
            false
          )
          setLoading(false)
          return
        }
      }

      const payload = itemsToAdjust.map((itemLst) =>
        mapSingleItemToPayload(itemLst.index, itemLst.item.asnDetailId)
      )

      if (payload.length === 0) {
        openSnackBar('No hay ajustes a informar.', true)
        setLoading(false)
        return
      }

      await sendAdjustmentsApi(putInboundAdjustSku(payload))
      openSnackBar('Ajuste finalizado con éxito', true)
      if (onDataReload) {
        onDataReload()
      } else {
        await requestInboundAdjust(
          pagination.page,
          pagination.pageSize,
          selectedFilters
        )
      }
    } catch (error) {
      openSnackBar(`Error al finalizar el ajuste: ${error}`, false)
    } finally {
      setLoading(false)
    }
  }

  // --- VALORES EXPUESTOS ---
  return {
    loading,
    saveButtonState,
    items,
    totalItems,
    pagination,
    rowQuantities,
    rowComments,
    fetchInitialData,
    handlePaginationChange,
    handleSearch,
    handleQuantityChange,
    handleCommentChange,
    saveLine,
    saveAll,
    snackbar: {
      isActive,
      message,
      isSuccess
    }
  }
}
